import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  ControllerCalendar,
  ControllerDropdownInput,
  ControllerTextarea,
  ControllerTextInput,
} from "components/basic/controller-inputs/ControllerInputs";
import {
  MdmObjectDto,
  MdmObjectJobDtoInput,
  SeverityType,
  StatusType,
  useCreateJobMutation,
  useUpdateJobMutation,
} from "gql/generated/types";
import { GET_JOBS_BY_OBJECT_ID, GET_JOBS_BY_PROJECT_ID } from "gql/job.operations";
import { Button } from "primereact/button";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { T } from "services/lang/LanguageService";
import { z } from "zod";
import { priorityType, statusType } from "../utils/const";
import { apolloErrorToaster, showOptionSeverity, showOptionTag, successToaster } from "../utils/utils";

const schema = z.object({
  status: z.string().min(1, T("word.required-fields")),
  severity: z.string().min(1, T("word.required-fields")),
  description: z.string().min(1, T("word.required-fields")),
  title: z.string().min(1, T("word.required-fields")),
  startDate: z.date(),
  endDate: z.date(),
  objectId: z.string().optional(),
  id: z.number().optional(),
});

type JobFormProps = {
  object: MdmObjectDto;
  closeDialog: () => void;
  job: MdmObjectJobDtoInput | null;
};

const JobForm: React.FC<JobFormProps> = ({ object, closeDialog, job }) => {
  const { projectId } = useParams();
  const defaultValues: MdmObjectJobDtoInput = {
    status: StatusType.Todo,
    severity: SeverityType.Medium,
    description: "",
    title: "",
    startDate: new Date(),
    endDate: new Date(),
  };

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<MdmObjectJobDtoInput>({
    defaultValues: job ?? defaultValues,
    resolver: zodResolver(schema),
  });

  const [createJobMutation] = useCreateJobMutation({
    onCompleted: () => successToaster(T("alert.job-created-successfully")),
    onError: apolloErrorToaster,
  });

  const [updateJobMutation] = useUpdateJobMutation({
    onCompleted: () => successToaster(T("alert.job-updated-successfully")),
    onError: apolloErrorToaster,
  });

  const handleCreateJob: SubmitHandler<MdmObjectJobDtoInput> = formData => {
    createJobMutation({
      variables: {
        objectId: object.id!,
        input: formData,
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_JOBS_BY_OBJECT_ID, variables: { id: object.id } }, oldData => ({
          allJobsByObjectId: [...oldData.allJobsByObjectId, qData?.createJob],
        }));
        cache.updateQuery({ query: GET_JOBS_BY_PROJECT_ID, variables: { id: projectId } }, oldData =>
          oldData
            ? {
                allJobsByProjectId: [...oldData.allJobsByProjectId, qData?.createJob],
              }
            : null
        );
        cache.modify({
          id: cache.identify({ id: object?.id, __typename: "MdmObjectDTO" }),
          fields: {
            info: info => {
              return {
                ...info,
                jobsCount: info.jobsCount + 1,
              };
            },
          },
        });
      },
    });
    reset();
    closeDialog();
  };

  const handleUpdateJob: SubmitHandler<MdmObjectJobDtoInput> = formData => {
    updateJobMutation({
      variables: {
        input: formData,
      },
    });
    reset();
    closeDialog();
  };

  return (
    <form className="flex flex-wrap">
      <Controller
        name="status"
        control={control}
        render={({ field }) => (
          <ControllerDropdownInput
            field={field}
            errors={errors}
            label={T("word.status")}
            col={4}
            options={statusType}
            optionLabel="translationKey"
            itemTemplate={showOptionTag}
            valueTemplate={showOptionTag}
            optionValue="value"
            placeholder={T("word.select-one")}
          />
        )}
      />
      <Controller
        name="severity"
        control={control}
        render={({ field }) => (
          <ControllerDropdownInput
            field={field}
            errors={errors}
            label={T("word.priority")}
            col={4}
            options={priorityType}
            optionLabel="translationKey"
            itemTemplate={showOptionSeverity}
            valueTemplate={showOptionSeverity}
            optionValue="value"
            placeholder={T("word.select-one")}
          />
        )}
      />
      <Controller
        name="title"
        control={control}
        render={({ field }) => (
          <ControllerTextInput field={field} errors={errors} label={T("word.title")} col={4} />
        )}
      />
      <Controller
        name="startDate"
        control={control}
        render={({ field }) => (
          <ControllerCalendar
            field={field}
            errors={errors}
            label={T("word.start-date")}
            col={4}
            dateFormat="yy-mm-dd"
          />
        )}
      />
      <Controller
        name="endDate"
        control={control}
        render={({ field }) => (
          <ControllerCalendar
            field={field}
            errors={errors}
            label={T("word.end-date")}
            col={4}
            dateFormat="yy-mm-dd"
          />
        )}
      />
      <Controller
        name="description"
        control={control}
        render={({ field }) => (
          <ControllerTextarea field={field} errors={errors} label={T("word.description")} col={12} />
        )}
      />
      {job ? (
        <Button
          label={T("action.update")}
          className="mt-3 ml-2 p-button-success"
          onClick={handleSubmit(handleUpdateJob)}
        />
      ) : (
        <Button
          label={T("action.create")}
          className="mt-3 ml-2 p-button-success"
          onClick={handleSubmit(handleCreateJob)}
        />
      )}
    </form>
  );
};

export default JobForm;
